import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';

import {map} from 'rxjs/operators';

import {MapUtils} from '../../shared/utils/mapper';
import { Customer, ExportXLSXProps } from 'src/app/models/customer.model';
import {FXResponse} from 'src/app/models/response.model';
import {date2String, standardizeParam} from 'src/app/shared/utils/helper';
// import { AutoOrder } from 'src/app/models/auto-order.model';
import {APP_CONFIG} from 'src/app/shared/common/config';
import {ExchangeTransaction, Transaction} from 'src/app/models/transaction.model';
import {AccountsModel} from '../../models/accounts.model';
import {AddressModel, DealsModel } from '../../models/cms.model';
import { downloadBlob } from 'src/app/shared/utils/export-csv';
import { MamFollowerDetailModel, MamFollowerModel } from 'src/app/models/mam.approval.model';
import { BaseResponse, CmsDetailAccountProps, CmsDetailAccountResponse } from '../../models/shared.interface';
import { ExportTraderRequest } from './models/trader.interface';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { DatePipe } from '@angular/common';
import { COUNTRY_CODES } from '../../shared/utils/constants';

@Injectable()
export class CmsService {
  // URL to web api.
  private customerUrl = APP_CONFIG.BASE_API_LINK.BASE + '/customers';

  constructor(private http: HttpClient, private readonly datePipe: DatePipe) {}

  getAccounts(params): Observable<FXResponse<AccountsModel>> {
    return this.http
      .get(`${APP_CONFIG.BASE_API_LINK.CMS}/accounts?${standardizeParam(params)}`)
      .pipe(
        map((res: any) => {
          const data = res.data.accounts;
          const response = new FXResponse<AccountsModel[]>().deserialize(res);
          response.paging = res.data.paging;
          response.data = data.map((account: any) => {
            const c = MapUtils.deserialize(AccountsModel, account);
            return c;
          });
          return response;
        }),
      );
  }

  getSuspendedListUser(params: any): Observable<FXResponse<Customer[]>> {
    return this.http
      .get(`${APP_CONFIG.BASE_API_LINK.CMS}/suspend?${standardizeParam(params)}`)
      .pipe(
        map((res: any) => {
          const data = res.data.suspend_users;
          const response = new FXResponse<Customer[]>().deserialize(res);
          response.paging = res.data.paging;
          response.data = data
          return response;
        }),
      );
  }

  unsuspendUser(data: any): Observable<any> {
    const url = `${APP_CONFIG.BASE_API_LINK.CMS}/suspend`;
    return this.http.delete(url, { body: data }).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  exportCustomers(params: any): Observable<void> {
    params = Object.assign(params, {
      is_export: 1
    })
    return this.http
      .get(`${APP_CONFIG.BASE_API_LINK.CMS}/users?${standardizeParam(params)}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          let fileName: string = `cms-${date2String(new Date())}.xlsx`;
          downloadBlob(res.body, fileName);
        })
      )
  }

  getCustomers(params: any = {}): Observable<FXResponse<Customer[]>> {
    return this.http
      .get(`${APP_CONFIG.BASE_API_LINK.CMS}`, {params})
      .pipe(
        map((res: any) => {
          const data = res.data.data;
          const response = new FXResponse<Customer[]>().deserialize(res);
          response.paging = res.data.paging;
          response.data = data.map((customer: any) => {
            const c = MapUtils.deserialize(Customer, customer);
            return c;
          });
          return response;
        }),
      );
    /*return new Observable<FXResponse<Customer[]>>((observer) => {
      const res: any = DUMMY_DATA.CMS.USERS;
      observer.next(res);
      observer.complete();
    }).pipe(
      map((res: any) => {
        const data = res.data;
        const response = new FXResponse<Customer[]>().deserialize(res);
        response.paging = res.paging;
        response.data = data.map((customer: any) => {
          const c = MapUtils.deserialize(Customer, customer);
          return c;
        });
        return response;
      })
    );*/
  }

  #saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    FileSaver.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
  }

  exportToExcelWithHeader({header, data, filename = 'cms-export'}: ExportXLSXProps) {
    const wb = XLSX.utils.book_new();
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
    XLSX.utils.sheet_add_aoa(ws, header);
    XLSX.utils.sheet_add_json(ws, data, { origin: 'A2', skipHeader: true });
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${filename}.xlsx`);
  }

  exportAsExcelFileRaw(json: any[], excelFileName: string): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
    const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.#saveAsExcelFile(excelBuffer, excelFileName);
  }

  exportAllByClient() {
    this.getCustomers().subscribe((data) => {
      const sheet = [];
      const userType = {
        0: 'IB',
        '1': 'IB',
        '2': 'IB Trader',
        '3': 'Organic Trader'
      };
      if(data && data.data) {
        data.data.forEach((v) => {
          sheet.push({
            mt5ID: v.mt5Account,
            openingDate: this.datePipe.transform(v.mt5CreateTime, 'yyyy/MM/dd'),
            parentIBID: v.ibId,
            mampamm: v.isMam === 1 ? 'Yes' : 'No',
            mt5UserType: userType[v.userType],
            username: v.getUsername(),
            usernameJP: v.getUsernameJP(),
            email: v.email,
            kyc: v.getKYCByText(),
            country: COUNTRY_CODES[v.country],
            activation: v.verifiedEmail ? 'Yes' : 'No',
            status: v.active === 1 ? 'Active' : 'Inactive'
          })
        })
        this.exportToExcelWithHeader({
          header: [['MT5 ID', 'Opening Data', 'Parent IB ID', 'MAMPAMM', 'MT5 User Type', 'Username', 'Username(JP)', 'Email', 'KYC', 'Country', 'Activated', 'Status']],
          data: sheet
        })
      }
    })
  }

  create(customer: Customer): Observable<any> {
    const url = `${this.customerUrl}`;
    const jsonUser = MapUtils.serialize(customer);
    return this.http.post(url, jsonUser).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  update(customer: Customer): Observable<any> {
    const url = `${this.customerUrl}`;
    const jsonUser = MapUtils.serialize(customer);
    return this.http.put(url, jsonUser).pipe(
      map((res: any) => {
        return res;
      })
    );
  }
  setAffiliate(data: any): Observable<any> {
    const url = `${this.customerUrl}/affiliates/enable`;
    return this.http.post(url, data).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  delete(ids: number[]): Observable<FXResponse<any>> {
    const url = `${this.customerUrl}`;
    return this.http.request('delete', url, { body: { id: ids } }).pipe(
      map((res: any) => {
        return new FXResponse<any>().deserialize(res);
      })
    );
  }

  getTransactions<T>(
    params: any,
    type: string,
    model: any
  ): Observable<FXResponse<T[]>> {
    return this.http
      .get(
        `${
          APP_CONFIG.BASE_API_LINK.CMS
        }/${type}-transactions?${standardizeParam(params)}`
      )
      .pipe(
        map((res: any) => {
          const data = res.data[`${type.replace('-', '_')}_transactions`];
          const response = new FXResponse<T[]>().deserialize(res);
          response.paging = res.data.paging;
          response.data = data.map((trans: any) =>
            MapUtils.deserialize(model, trans)
          );
          return response;
        })
      );
    /*return new Observable<FXResponse<T[]>>((observer) => {
      const res: any =
        DUMMY_DATA.CMS[`${type.replace('-', '_')}`.toUpperCase()];
      observer.next(res);
      observer.complete();
    }).pipe(
      map((res: any) => {
        const data = res.data[`${type.replace('-', '_')}_transactions`];
        const response = new FXResponse<T[]>().deserialize(res);
        response.paging = res.data.paging;
        response.data = data.map((trans: any) =>
          MapUtils.deserialize(model, trans)
        );
        return response;
      })
    );*/
  }

  getBalanceTransactions(params: any): Observable<FXResponse<Transaction[]>> {
    return this.http
      .get(
        `${
          APP_CONFIG.BASE_API_LINK.CMS
        }/balance-transactions?${standardizeParam(params)}`
      )
      .pipe(
        map((res: any) => {
          const data = res.data.balance_transactions;
          const response = new FXResponse<Transaction[]>().deserialize(res);
          response.paging = res.data.paging;
          response.data = data.map((trans: any) =>
            MapUtils.deserialize(Transaction, trans)
          );
          return response;
        })
      );
    /*return new Observable<FXResponse<Transaction[]>>((observer) => {
      const res: any = DUMMY_DATA.CMS.BALANCE;
      observer.next(res);
      observer.complete();
    }).pipe(
      map((res: any) => {
        const data = res.data.balance_transactions;
        const response = new FXResponse<Transaction[]>().deserialize(res);
        response.paging = res.data.paging;
        response.data = data.map((trans: any) =>
          MapUtils.deserialize(Transaction, trans)
        );
        return response;
      })
    );*/
  }

  getExchangeTransactions(
    params: any
  ): Observable<FXResponse<ExchangeTransaction[]>> {
    return this.http
      .get(
        `${
          APP_CONFIG.BASE_API_LINK.CMS
        }/exchange-transactions?${standardizeParam(params)}`
      )
      .pipe(
        map((res: any) => {
          const data = res.data.exchange_transactions;
          const response = new FXResponse<
            ExchangeTransaction[]
          >().deserialize(res);
          response.paging = res.data.paging;
          response.data = data.map((trans: any) =>
            MapUtils.deserialize(ExchangeTransaction, trans)
          );
          return response;
        })
      );
  }
  getAccountStatistics(): Observable<FXResponse<any>> {
    const url = `${APP_CONFIG.BASE_API_LINK.CMS}/newest`;
    return this.http.get(url).pipe(
      map((res: any) => {
        const response = new FXResponse<any>().deserialize(res);
        return response;
      })
    );
  }
  getIBInfotByUser(userId, isNotLoading = false): Observable<FXResponse<any[]>> {
    const url = `${APP_CONFIG.BASE_API_LINK.IB}?user_id=${userId}`;
    return this.http.get(url, {
      headers: {
        '[onlyclient]notLoading': isNotLoading ? 'true' : 'false'
      }
    }).pipe(
      map((res: any) => {
        const response = new FXResponse<any[]>().deserialize(res);
        response.data = res.data.ib_list;
        return response;
      })
    );
  }

  getBonusEventHistory(params: any): Observable<FXResponse<Customer[]>> {
    return this.http
      .get(`${APP_CONFIG.BASE_API_LINK.CMS}/bonus/events?${standardizeParam(params)}`)
      .pipe(
        map((res: any) => {;
          const response = new FXResponse<Customer[]>().deserialize(res);
          response.data = res.data.bonus_events;
          response.paging = res.data.paging;
          return response;
        }),
      );
  }
  updateBonusEvent(body: any): Observable<any> {
    return this.http.put(`${APP_CONFIG.BASE_API_LINK.CMS}/bonus/events`, body).pipe(
      map((res: any) => {
        const response = new FXResponse<any>().deserialize(res);
        return response;
      })
    );
  }

  getAddressInfoByUser(userId): Observable<FXResponse<any>> {
    const url = `${APP_CONFIG.BASE_API_LINK.CMS}/address?user_id=${userId}`;
    return this.http.get(url).pipe(
      map((res: any) => {
        const response = new FXResponse<any>().deserialize(res);
        response.data = MapUtils.deserialize(
          AddressModel,
          res.data.address_info
        );
        return response;
      })
    );
  }

  updateCustomerInfo(data: any): Observable<any> {
    return this.http.post(`${APP_CONFIG.BASE_API_LINK.CMS}/address`, data);
  }
  getDeals(params: any): Observable<FXResponse<DealsModel[]>> {
    return this.http
      .get(`${APP_CONFIG.BASE_API_LINK.CMS}/deals?${standardizeParam(params)}`)
      .pipe(
        map((res: any) => {
          const response = new FXResponse<DealsModel[]>().deserialize(res);
          response.paging = res.data.paging;
          response.data = res.data.deals;
          return response;
        }),
      );
  }

  getMamFollowers(params: any): Observable<FXResponse<any>> {
    const url = `${APP_CONFIG.BASE_API_LINK.CMS}/mam-followers?${standardizeParam(params)}`;
    return this.http
      .get(url)
      .pipe(
        map((res: any) => {
          const response = new FXResponse<any>().deserialize(res);
          response.paging = res.data.paging;
          response.data = response.data.follower_mam_accounts.map(v =>
            MapUtils.deserialize(MamFollowerModel, v));
          return response;
        }),
      );
  }

  getMamDetail(params: any): Observable<FXResponse<any>> {
    const url = `${APP_CONFIG.BASE_API_LINK.CMS}/account-info?${standardizeParam(params)}`;
    return this.http
      .get(url)
      .pipe(
        map((res: any) => {
          let response = new FXResponse<any>().deserialize(res);
          response.data=MapUtils.deserialize(MamFollowerDetailModel, response.data)
          return response;
        })
      );
  }

  getBonusDetail(params: any): Observable<FXResponse<any>> {
    const url = `${APP_CONFIG.BASE_API_LINK.CMS}/bonus/events?${standardizeParam(params)}`;
    return this.http
      .get(url)
      .pipe(
        map((res: any) => {
          const response = new FXResponse<any>().deserialize(res);
          return response;
        })
      );
  }

  updateMamFollower(item: any): Observable<FXResponse<any>> {
    const url = `${APP_CONFIG.BASE_API_LINK.IB}/mam-followers`;
    const body = MapUtils.serialize(item);
    return this.http.patch(url, body).pipe(
      map((res: any) => {
        const response = new FXResponse<any>().deserialize(res);
        return response;
      })
    );
  }
  processCloseAllPosition(item: any): Observable<FXResponse<any>> {
    const url = `${APP_CONFIG.BASE_API_LINK.IB}/mam-followers/positions/close-all`;
    const body = MapUtils.serialize(item);
    return this.http.post(url, body).pipe(
      map((res: any) => {
        const response = new FXResponse<any>().deserialize(res);
        return response;
      })
    );
  }

  exportTraders(params: ExportTraderRequest): Observable<void> {
    params = Object.assign(params, {
      is_export: 1
    })
    return this.http
      .get(`${APP_CONFIG.BASE_API_LINK.CMS}/traders?${standardizeParam(params)}`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          let fileName: string = `trader-${date2String(new Date())}.xlsx`;
          downloadBlob(res.body, fileName);
        })
      )
  }

  getTraders(params: any): Observable<FXResponse<Customer[]>> {
    return this.http
      .get(`${APP_CONFIG.BASE_API_LINK.CMS}/traders?${standardizeParam(params)}`)
      .pipe(
        map((res: any) => {
          const data = res.data.records;
          const response = new FXResponse<Customer[]>().deserialize(res);
          response.paging = res.data.paging;
          response.data = data.map((customer: any) => {
            const c = MapUtils.deserialize(Customer, customer);
            return c;
          });
          return response;
        }),
      );
  }

  getCmsDetailAccounts(params: any = {}): Observable<BaseResponse<CmsDetailAccountResponse>> {
    const accountType = (props: CmsDetailAccountProps) => {
      const account = ['Standard', 'Micro', 'Pro', 'STD-RB', 'MIC-RB', 'Pro-RB', 'Standard']
      if(props.is_mam) {
        return 'Mam';
      }
      return account[props.account_type];
    }
    return this.http.get<BaseResponse<CmsDetailAccountResponse>>(`${APP_CONFIG.BASE_API_LINK.CMS}/accounts`, { params }).pipe(
      map((res) => {
        res.data.accounts.forEach((e) => {
          e.type = accountType(e)
        })
        return res;
      })
    );
  }

  getCmsDetailAddressInfoByUser(params: any = {}): Observable<any> {
    const url = `${APP_CONFIG.BASE_API_LINK.CMS}/address`;
    return this.http.get(url, {params}).pipe(
      map((res: any) => {

        return res;
      })
    );
  }
}
